import http from './http';

/**
 * Master Data Configuration
 * Maps frontend master data tabs to backend API endpoints and field structures
 */

export const masterDataConfig = {
  drugs: {
    endpoint: '/api/v1/master-data/drugs',
    displayName: 'Drugs',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Drug Name', type: 'text', required: true },
      { key: 'genericName', label: 'Generic Name', type: 'text' },
      { key: 'brandName', label: 'Brand Name', type: 'text' },
      { 
        key: 'manufacturer', 
        label: 'Manufacturer', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/manufacturers',
          displayName: 'Manufacturers',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'city',
          tertiaryField: 'state',
          showCode: true
        }
      },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'strength', label: 'Strength', type: 'text' },
      { key: 'unit', label: 'Unit', type: 'text' },
      { key: 'form', label: 'Form', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'genericName', 'manufacturer', 'category', 'strength', 'form', 'isActive']
  },
  
  cities: {
    endpoint: '/api/v1/master-data/cities',
    displayName: 'Cities',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'City Name', type: 'text', required: true },
      { key: 'state', label: 'State', type: 'text', required: true },
      { key: 'country', label: 'Country', type: 'text', default: 'India' },
      { key: 'code', label: 'Code', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'state', 'country', 'code', 'isActive']
  },

  vaccines: {
    endpoint: '/api/v1/master-data/vaccines',
    displayName: 'Vaccines',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Vaccine Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { 
        key: 'manufacturer', 
        label: 'Manufacturer', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/manufacturers',
          displayName: 'Manufacturers',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'city',
          tertiaryField: 'state',
          showCode: true
        }
      },
      { key: 'type', label: 'Type', type: 'text' },
      { key: 'ageGroup', label: 'Age Group', type: 'text' },
      { key: 'dosage', label: 'Dosage', type: 'text' },
      { key: 'route', label: 'Route', type: 'text' },
      { key: 'schedule', label: 'Schedule', type: 'text' },
      { key: 'storage', label: 'Storage', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'manufacturer', 'type', 'ageGroup', 'dosage', 'route', 'isActive']
  },

  specialties: {
    endpoint: '/api/v1/master-data/specialties',
    displayName: 'Specialties',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Specialty Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'department', label: 'Department', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'department', 'description', 'isActive']
  },

  'test-categories': {
    endpoint: '/api/v1/master-data/test-categories',
    displayName: 'Test Categories',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Category Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'department', label: 'Department', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'department', 'description', 'isActive']
  },

  tests: {
    endpoint: '/api/v1/master-data/tests',
    displayName: 'Tests',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Test Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { 
        key: 'category', 
        label: 'Category', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/test-categories',
          displayName: 'Test Categories',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'department',
          showCode: true
        }
      },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'unit', label: 'Unit', type: 'text' },
      { key: 'methodology', label: 'Methodology', type: 'text' },
      { key: 'specimen', label: 'Specimen', type: 'text' },
      { key: 'turnaroundTime', label: 'Turnaround Time', type: 'text' },
      { key: 'price', label: 'Price', type: 'number' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'category', 'price', 'unit', 'methodology', 'isActive']
  },

  'blood-banks': {
    endpoint: '/api/v1/master-data/blood-banks',
    displayName: 'Blood Banks',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Blood Bank Name', type: 'text', required: true },
      { key: 'address', label: 'Address', type: 'textarea' },
      { 
        key: 'city', 
        label: 'City', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/cities',
          displayName: 'Cities',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'state',
          tertiaryField: 'country',
          showCode: false
        }
      },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'pincode', label: 'Pincode', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'licenseNumber', label: 'License Number', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'city', 'state', 'phone', 'email', 'licenseNumber', 'isActive']
  },

  manufacturers: {
    endpoint: '/api/v1/master-data/manufacturers',
    displayName: 'Medicine Manufacturers',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Manufacturer Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { key: 'address', label: 'Address', type: 'textarea' },
      { 
        key: 'city', 
        label: 'City', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/cities',
          displayName: 'Cities',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'state',
          tertiaryField: 'country',
          showCode: false
        }
      },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'country', label: 'Country', type: 'text', default: 'India' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'website', label: 'Website', type: 'url' },
      { key: 'licenseNumber', label: 'License Number', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'city', 'state', 'phone', 'email', 'isActive']
  },

  villages: {
    endpoint: '/api/v1/master-data/villages',
    displayName: 'Villages',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Village Name', type: 'text', required: true },
      { 
        key: 'area', 
        label: 'Area', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/areas',
          displayName: 'Areas',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'city',
          tertiaryField: 'state',
          showCode: true
        }
      },
      { 
        key: 'city', 
        label: 'City', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/cities',
          displayName: 'Cities',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'state',
          tertiaryField: 'country',
          showCode: false
        }
      },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'pincode', label: 'Pincode', type: 'text' },
      { key: 'population', label: 'Population', type: 'number' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'area', 'city', 'state', 'pincode', 'population', 'isActive']
  },

  'imaging-master': {
    endpoint: '/api/v1/imaging-master',
    displayName: 'Imaging Master',
    icon: 'Database',
    customComponent: 'ImagingMasterTab', // Use custom component instead of generic
    fields: [],
    tableColumns: []
  },

  amcs: {
    endpoint: '/api/v1/master-data/amcs',
    displayName: 'AMCS',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'AMCS Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'category', 'description', 'isActive']
  },

  areas: {
    endpoint: '/api/v1/master-data/areas',
    displayName: 'Areas',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Area Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { 
        key: 'city', 
        label: 'City', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/cities',
          displayName: 'Cities',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'state',
          tertiaryField: 'country',
          showCode: false
        }
      },
      { key: 'state', label: 'State', type: 'text' },
      { key: 'pincode', label: 'Pincode', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'city', 'state', 'pincode', 'isActive']
  },

  diseases: {
    endpoint: '/api/v1/master-data/diseases',
    displayName: 'Diseases',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Disease Name', type: 'text', required: true },
      { key: 'code', label: 'Code', type: 'text' },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'icdCode', label: 'ICD-10 Code', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { 
        key: 'specialty', 
        label: 'Specialty', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/specialties',
          displayName: 'Specialties',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'department',
          showCode: true
        }
      },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'code', 'category', 'icdCode', 'description', 'specialty']
  },

  medicines: {
    endpoint: '/api/v1/master-data/medicines',
    displayName: 'Medicines',
    icon: 'Database',
    fields: [
      { key: 'name', label: 'Medicine Name', type: 'text', required: true },
      { 
        key: 'drug', 
        label: 'Drug', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/drugs',
          displayName: 'Drugs',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'genericName',
          tertiaryField: 'brandName',
          showCode: false
        }
      },
      { 
        key: 'manufacturer', 
        label: 'Manufacturer', 
        type: 'linked',
        linkedConfig: {
          endpoint: '/api/v1/master-data/manufacturers',
          displayName: 'Manufacturers',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'city',
          tertiaryField: 'state',
          showCode: true
        }
      },
      { key: 'doseQuantity', label: 'Dose Quantity', type: 'number', required: true, default: 1 },
      { 
        key: 'patientGroup', 
        label: 'Patient Group', 
        type: 'select',
        options: [
          { value: 'adult', label: 'Adult' },
          { value: 'child', label: 'Child' }
        ],
        required: true,
        default: 'adult'
      },
      { key: 'timing', label: 'Timing', type: 'timing', fields: [
        { key: 'morning', label: 'Morning', type: 'boolean', default: false },
        { key: 'noon', label: 'Noon', type: 'boolean', default: false },
        { key: 'night', label: 'Night', type: 'boolean', default: false }
      ] },
      { 
        key: 'route', 
        label: 'Route of Administration', 
        type: 'select',
        options: [
          { value: 'oral', label: 'Oral' },
          { value: 'sublingual', label: 'Sublingual' },
          { value: 'buccal', label: 'Buccal' },
          { value: 'inhalation', label: 'Inhalation' },
          { value: 'topical', label: 'Topical' },
          { value: 'transdermal', label: 'Transdermal' },
          { value: 'intravenous', label: 'Intravenous (IV)' },
          { value: 'intramuscular', label: 'Intramuscular (IM)' },
          { value: 'subcutaneous', label: 'Subcutaneous (SC)' },
          { value: 'rectal', label: 'Rectal' },
          { value: 'vaginal', label: 'Vaginal' },
          { value: 'otic', label: 'Otic (Ear)' },
          { value: 'ophthalmic', label: 'Ophthalmic (Eye)' },
          { value: 'nasal', label: 'Nasal' },
          { value: 'other', label: 'Other' }
        ]
      },
      { 
        key: 'source', 
        label: 'Source', 
        type: 'select',
        options: [
          { value: 'internal', label: 'Internal' },
          { value: 'external', label: 'External' }
        ],
        default: 'internal'
      },
      { key: 'dosesPerDay', label: 'Doses Per Day', type: 'number', default: 1 },
      { key: 'mgPerUnit', label: 'MG Per Unit', type: 'number' },
      { key: 'dosePerKg', label: 'Dose Per KG', type: 'number' },
      { 
        key: 'diseases', 
        label: 'Diseases', 
        type: 'multiselect',
        linkedConfig: {
          endpoint: '/api/v1/master-data/diseases',
          displayName: 'Diseases',
          displayField: 'name',
          searchField: 'name',
          secondaryField: 'category',
          showCode: true
        }
      },
      { key: 'instruction', label: 'Instructions', type: 'textarea' },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
      { key: 'days', label: 'Days', type: 'number', default: 1 },
      { key: 'sideEffects', label: 'Side Effects', type: 'textarea' },
      { key: 'stopReason', label: 'Stop Reason', type: 'text' },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['name', 'drug', 'manufacturer', 'doseQuantity', 'patientGroup', 'route', 'dosesPerDay', 'mgPerUnit', 'days', 'isActive']
  },

  slogans: {
    endpoint: '/api/v1/master-data/slogans',
    displayName: 'Slogans',
    icon: 'Database',
    fields: [
      { key: 'slogan', label: 'Slogan', type: 'text', required: true },
      { key: 'isActive', label: 'Active', type: 'boolean', default: true }
    ],
    tableColumns: ['slogan']
  }
};

/**
 * Master Data API Service using the existing HTTP client
 */
class MasterDataAPIService {
  constructor(endpoint) {
    this.endpoint = endpoint;
  }

  // Get all records with pagination and search
  async getAll(params = {}) {
    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || '',
      isActive: params.isActive !== undefined ? params.isActive : true,
      sortBy: params.sortBy || 'name',
      sortOrder: params.sortOrder || 'asc'
    };

    const response = await http.get(this.endpoint, { params: queryParams });
    return response.data.data; // Response structure: { success: true, data: { records, pagination } }
  }

  // Get single record by ID
  async getById(id) {
    const response = await http.get(`${this.endpoint}/${id}`);
    return response.data.data; // Response structure: { success: true, data: record }
  }

  // Create new record
  async create(data) {
    const response = await http.post(this.endpoint, data);
    return response.data.data; // Response structure: { success: true, message: string, data: record }
  }

  // Update existing record
  async update(id, data) {
    const response = await http.put(`${this.endpoint}/${id}`, data);
    return response.data.data; // Response structure: { success: true, message: string, data: record }
  }

  // Delete record (soft delete)
  async delete(id) {
    const response = await http.delete(`${this.endpoint}/${id}`);
    return response.data.data; // Response structure: { success: true, message: string, data: record }
  }

  // Search records
  async search(searchTerm, params = {}) {
    return this.getAll({
      ...params,
      search: searchTerm
    });
  }

  // Get active records only
  async getActive(params = {}) {
    return this.getAll({
      ...params,
      isActive: true
    });
  }
}

// Factory function to create service instances
export const createMasterDataService = (endpoint) => {
  return new MasterDataAPIService(endpoint);
};

// Get master data configuration by key
export const getMasterDataConfig = (key) => masterDataConfig[key];

// Get all master data options for dropdown
export const getMasterDataOptions = () => {
  return Object.entries(masterDataConfig)
    .map(([key, config]) => ({
      id: key,
      label: config.displayName,
      icon: config.icon
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Pre-configured service instances for each master data type
export const masterDataServices = {
  amcs: new MasterDataAPIService('/api/v1/master-data/amcs'),
  areas: new MasterDataAPIService('/api/v1/master-data/areas'),
  bloodBanks: new MasterDataAPIService('/api/v1/master-data/blood-banks'),
  cities: new MasterDataAPIService('/api/v1/master-data/cities'),
  diseases: new MasterDataAPIService('/api/v1/master-data/diseases'),
  drugs: new MasterDataAPIService('/api/v1/master-data/drugs'),
  manufacturers: new MasterDataAPIService('/api/v1/master-data/manufacturers'),
  medicines: new MasterDataAPIService('/api/v1/master-data/medicines'),
  slogans: new MasterDataAPIService('/api/v1/master-data/slogans'),
  specialties: new MasterDataAPIService('/api/v1/master-data/specialties'),
  testCategories: new MasterDataAPIService('/api/v1/master-data/test-categories'),
  tests: new MasterDataAPIService('/api/v1/master-data/tests'),
  vaccines: new MasterDataAPIService('/api/v1/master-data/vaccines'),
  villages: new MasterDataAPIService('/api/v1/master-data/villages')
};


export default masterDataConfig;
